// DS1.10.5
import React, { useCallback } from 'react';
import c from 'classnames';
import { useReverseTheme } from '@wix/da-react-context/pkg/ThemeContext';
import PopperPositionedPopover, {
  Props as PopperPositionedPopoverProps,
} from '../../../popper/PopperPositionedPopover';
import { usePopoverOnInteraction } from '../../../popper/hooks/usePopoverOnInteraction';
import PopoverWrapper from '../../../popper/PopoverWrapper';
import s from './CustomTooltip.scss';

interface Props extends Omit<
  PopperPositionedPopoverProps,
  | 'renderPopover'
  | 'isVisible'
  | 'onReferenceClick'
  | 'onReferenceMouseEnter'
  | 'onReferenceMouseLeave'
> {
  /**
   * The tooltip's content
   */
  content: React.ReactNode;

  /**
   * Limits the width of the tooltip.
   */
  width?: number;

  /**
   * Force tooltip to be visible.
   */
  forceVisible?: boolean;
}

const CustomTooltip: React.FC<Props> = ({
  content,
  children,
  width,
  forceVisible = false,
  className,
  ...rest
}) => {
  const { isVisible, onClick, onMouseEnter, onMouseLeave, onClose } = usePopoverOnInteraction({
    listenOnClicks: true,
    listenOnHover: true,
  });
  const reverseTheme = useReverseTheme();

  const renderPopover = useCallback(
    ({
      effectivePlacement,
      effectiveSecondaryPlacement,
      rootRef,
      arrowRef,
      contentStyle,
      arrowStyle,
      contentAttributes,
      arrowAttributes,
      updatePositioning,
    }) => (
      <PopoverWrapper
        contentClassName={s['popover-wrapper']}
        width={width}
        onClickOutside={onClose}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        effectivePlacement={effectivePlacement}
        effectiveSecondaryPlacement={effectiveSecondaryPlacement}
        rootRef={rootRef}
        arrowRef={arrowRef}
        contentStyle={contentStyle}
        arrowStyle={arrowStyle}
        contentAttributes={contentAttributes}
        arrowAttributes={arrowAttributes}
        updatePositioning={updatePositioning}
        forceTheme={reverseTheme}
      >
        {content}
      </PopoverWrapper>
    ),
    [width, content, onMouseEnter, onMouseLeave, onClose, reverseTheme]
  );

  return (
    <PopperPositionedPopover
      isVisible={isVisible || forceVisible}
      onReferenceClick={onClick}
      onReferenceMouseEnter={onMouseEnter}
      onReferenceMouseLeave={onMouseLeave}
      className={c(s['root'], className)}
      renderPopover={renderPopover}
      {...rest}
    >
      {children}
    </PopperPositionedPopover>
  );
};
CustomTooltip.displayName = 'CustomTooltip';

export default CustomTooltip;
